// CB:BEGIN ui-responsive-table
import React from 'react';
import { useDevice } from '../../hooks/useDevice';

type Column<T> = {
  key: keyof T | string;
  header: React.ReactNode;
  render?: (row: T, idx: number) => React.ReactNode;
  className?: string;
};

interface ResponsiveTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T, idx: number) => React.Key;
  empty?: React.ReactNode;
  className?: string;
}

function ResponsiveTable<T extends Record<string, any>>({ columns, rows, rowKey, empty, className }: ResponsiveTableProps<T>) {
  const { isMobileLayout } = useDevice();
  if (!rows || rows.length === 0) {
    return <div className="dashboard-card">{empty || 'No data'}</div>;
  }
  return (
    <div className={className}>
      {isMobileLayout ? (
        <div className="grid grid-cols-1 gap-3">
          {rows.map((row, idx) => (
            <div key={rowKey(row, idx)} className="project-card">
              <div className="grid grid-cols-1 gap-2">
                {columns.map((col, cIdx) => (
                  <div key={cIdx} className="flex items-start justify-between gap-4">
                    <div className="text-gray-500 text-sm font-medium">{col.header}</div>
                    <div className="text-gray-900 text-sm font-semibold">
                      {col.render ? col.render(row, idx) : String(row[col.key as keyof T] ?? '')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((c, idx) => (
                  <th key={idx} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {c.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((row, rIdx) => (
                <tr key={rowKey(row, rIdx)} className="hover:bg-gray-50">
                  {columns.map((c, cIdx) => (
                    <td key={cIdx} className={`px-4 py-3 text-sm ${c.className || ''}`}>
                      {c.render ? c.render(row, rIdx) : String(row[c.key as keyof T] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ResponsiveTable;
// CB:END ui-responsive-table

